import React from 'react';

import {
  AppBar as MuiAppBar,
  Avatar,
  Box,
  styled,
  Toolbar as MuiToolbar,
  Drawer as MuiDrawer,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  CSSObject
} from '@mui/material';
import {
  authService,
  OptionsPopover,
  ResponsiveImage
} from 'iron-forge-react-modules';
import {
  AttachMoney,
  ChevronLeft,
  ChevronRight,
  Group,
  Home,
  MoneyOff,
  Store,
  SwapHoriz,
  Warehouse,
  Settings,
  AccountBox,
  ShoppingCart
} from '@mui/icons-material';

import { routeDirectory } from 'modules/layer00';
import { PermissionWall } from 'modules/layer01';
import { useNavigate } from 'react-router-dom';
import * as BaseActivityTemplate from '../BaseActivityTemplate/BaseActivityTemplate';

import img1x from 'assets/images/q1Logo/1x.png';
import img2x from 'assets/images/q1Logo/2x.png';
import img3x from 'assets/images/q1Logo/3x.png';



const __openDrawerWidth = 240;
const __closedDrawerWidth = 64;



const openedMixin = (theme: Theme): CSSObject => ({
  width: __openDrawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: __closedDrawerWidth
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open'
})(({
  theme, open
}) => ({
  width: __openDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open'
})<{ open: boolean; }>(({
  theme, open
}) => ({
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(!open && {
    marginLeft: '64px',
    width: `calc(100% - ${64}px)`
  }),
  ...(open && {
    marginLeft: __openDrawerWidth,
    width: `calc(100% - ${__openDrawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end'
}));

const Toolbar = styled(MuiToolbar)(() => ({
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'space-between'
}));

const ChildBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'open'
})<{
  open: boolean;
}>(({ theme, open }) => ({
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  marginTop: theme.spacing(1),
  marginRight: theme.spacing(1),
  marginLeft: `calc(${__closedDrawerWidth}px + ${theme.spacing(1)})`,
  ...(open && {
    marginLeft: `calc(${__openDrawerWidth}px + ${theme.spacing(1)})`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2)
}));



export interface IProps {
  elRightContent?: JSX.Element;
  disableAuthRedirector?: boolean;
  children: React.ReactNode | React.ReactNode[];
}

export const FC: React.FC<IProps> = props => {
  const [open, setOpen] = React.useState(() => {
    // Initialize from localStorage, default to false if not set
    const savedState = localStorage.getItem('AdminActivityTemplateDrawerOpen');
    return savedState ? JSON.parse(savedState) : false;
  });
  const navigate = useNavigate();



  function handleClickOption(optionId: string) {
    if (optionId === 'logOut') {
      authService.logOut();
    }
  }


  const handleClickDrawerExpand = React.useCallback(
    () => {
      const newOpenState = !open;
      setOpen(newOpenState);
      // Persist the state to localStorage
      localStorage.setItem('AdminActivityTemplateDrawerOpen', JSON.stringify(newOpenState));
    },
    [open]
  );

  return (
    <BaseActivityTemplate.FC disableAuthRedirector={props.disableAuthRedirector}>
      <AppBar
        position="fixed"
        open={open}>
        <Toolbar>
          <div />

          <ResponsiveImage.FC urlSet={[
            img1x,
            img2x,
            img3x
          ]} />

          <ButtonContainer>
            {props.elRightContent}
          </ButtonContainer>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        open={open}>
        <DrawerHeader>
          <IconButton size="large" onClick={handleClickDrawerExpand}>
            {open ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />}
          </IconButton>
        </DrawerHeader>

        <Divider />

        <List sx={{ flex: 1 }}>
          <NavigationListItem
            open={open}
            label="Dashboard"
            elIcon={<Home />}
            onClick={() => navigate(routeDirectory.admin.root.path())} />

          <NavigationListItem
            open={open}
            label="E-comm orders"
            elIcon={<AttachMoney />}
            onClick={() => navigate(routeDirectory.admin.orders.path('ecommerce', 'awaitingApproval'))} />

          <NavigationListItem
            open={open}
            label="3PL orders"
            elIcon={<MoneyOff />}
            onClick={() => navigate(routeDirectory.admin.orders.path('thirdPartyLogistics', 'awaitingApproval'))} />

          <NavigationListItem
            open={open}
            label="Returns"
            elIcon={<SwapHoriz />}
            onClick={() => navigate(routeDirectory.admin.returnAuthorizations.list.path('awaitingApproval'))} />

          <NavigationListItem
            open={open}
            label="RMA Portal"
            elIcon={<SwapHoriz />}
            onClick={() => navigate(routeDirectory.admin.tier1ReturnAuthorizations.list.path('preAlertAcknowledged'))} />

          <PermissionWall.FC requiresPermissions={['inventory_moduleAccess']}>
            <NavigationListItem
              open={open}
              label="Inventory Management"
              elIcon={<Warehouse />}
              onClick={() => navigate(routeDirectory.admin.inventory.path('items'))} />
          </PermissionWall.FC>

          <NavigationListItem
            open={open}
            label="3PL Accounts"
            elIcon={<AccountBox />}
            onClick={() => navigate(routeDirectory.admin.thirdPartyAccounts.path())} />

          <NavigationListItem
            open={open}
            label="Stores"
            elIcon={<Store />}
            onClick={() => navigate(routeDirectory.partner.stores.path())} />

          <NavigationListItem
            open={open}
            label="User Management"
            elIcon={<Group />}
            onClick={() => navigate(routeDirectory.admin.appSettings.path())} />

          <NavigationListItem
            open={open}
            label="Order Management"
            elIcon={<ShoppingCart />}
            onClick={() => navigate(routeDirectory.admin.orderManagement.path('pendingWrites'))} />

          <NavigationListItem
            open={open}
            label="System Settings"
            elIcon={<Settings />}
            onClick={() => navigate(routeDirectory.admin.debug.path())} />

        </List>

        <Box sx={{ mb: 1 }}>
          <OptionsPopover.FC
            options={[
              {
                id: 'logOut',
                label: "Log Out"
              }
            ]}
            onClickOption={handleClickOption}
            elButtonIcon={<Avatar />} />
        </Box>
      </Drawer>

      <ChildBox
        component="main"
        open={open}>
        <DrawerHeader />

        {props.children}
      </ChildBox>
    </BaseActivityTemplate.FC>
  );
};



function NavigationListItem({
  onClick, elIcon, label, open
}: {
  onClick: () => void;
  elIcon: React.ReactNode;
  label: string;
  open: boolean;
}) {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onClick}>
        <ListItemIcon>
          {elIcon}
        </ListItemIcon>

        <ListItemText
          primaryTypographyProps={{
            sx: {
              fontSize: 12
            }
          }}
          primary={label}
          sx={{ opacity: open ? 1 : 0 }} />
      </ListItemButton>
    </ListItem>
  );
}
